if (!customElements.get('wlsn-header-nav')) {
  class WLSNHeaderNav extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        mobileMenuActive: 'mobile-menu-active',
        hasDropdown: 'js-wlsn-nav-has-dropdown',
        navList: 'js-wlsn-header-nav-list',
        navItem: 'js-wlsn-nav-item',
        navTrigger: 'js-wlsn-nav-trigger',
        menuToggle: 'js-wlsn-header-menu-toggle',
        cartDrawerTrigger: 'js-wlsn-header-cart-drawer-trigger',
        cartDrawer: 'js-wlsn-cart-drawer'
      };

      this.desktopBreakpoint = 1025;
      this.hoverCloseDelay = 200;
      this.hoverCloseTimer = null;

      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleMenuToggleClick = this.handleMenuToggleClick.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleNavLinkClick = this.handleNavLinkClick.bind(this);
      this.handleItemMouseEnter = this.handleItemMouseEnter.bind(this);
      this.handleItemMouseLeave = this.handleItemMouseLeave.bind(this);
      this.handleNavMouseEnter = this.handleNavMouseEnter.bind(this);
    }

    connectedCallback() {
      this.navList = this.querySelector(`.${this.classes.navList}`);
      this.navItems = Array.from(this.querySelectorAll(`.${this.classes.navItem}`));
      this.triggers = Array.from(this.querySelectorAll(`.${this.classes.navTrigger}`));
      this.menuToggle = this.querySelector(`.${this.classes.menuToggle}`);
      this.cartDrawerTrigger = this.querySelector(`.${this.classes.cartDrawerTrigger}`);

      this.handleSearchOpen = this.closeAll.bind(this);

      this.triggers.forEach((trigger) => trigger.addEventListener('click', this.handleTriggerClick));
      this.navItems.forEach((item) => {
        item.addEventListener('mouseenter', this.handleItemMouseEnter);
        item.addEventListener('mouseleave', this.handleItemMouseLeave);
      });
      if (this.navList) {
        this.navList.addEventListener('mouseenter', this.handleNavMouseEnter);
      }
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('resize', this.handleResize);
      // Close any open submenu when the search panel opens.
      document.addEventListener('wlsn:search:open', this.handleSearchOpen);

      if (this.menuToggle) {
        this.menuToggle.addEventListener('click', this.handleMenuToggleClick);
      }

      this.addEventListener('click', this.handleNavLinkClick);
      this.bindCartDrawerTrigger();
    }

    disconnectedCallback() {
      this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.handleTriggerClick));
      this.navItems.forEach((item) => {
        item.removeEventListener('mouseenter', this.handleItemMouseEnter);
        item.removeEventListener('mouseleave', this.handleItemMouseLeave);
      });
      if (this.navList) {
        this.navList.removeEventListener('mouseenter', this.handleNavMouseEnter);
      }
      this.clearHoverCloseTimer();
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('wlsn:search:open', this.handleSearchOpen);
      if (this.menuToggle) {
        this.menuToggle.removeEventListener('click', this.handleMenuToggleClick);
      }
      this.removeEventListener('click', this.handleNavLinkClick);
    }

    get isDesktop() {
      return window.innerWidth >= this.desktopBreakpoint;
    }

    handleTriggerClick(event) {
      const trigger = event.currentTarget;
      const item = trigger.closest(`.${this.classes.navItem}`);
      if (!item || !item.classList.contains(this.classes.hasDropdown)) return;

      const clickedLink = event.target.closest('a');
      if (clickedLink && !clickedLink.classList.contains(this.classes.navTrigger)) return;

      if (this.isDesktop) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const isActive = item.classList.contains(this.classes.active);

      item.classList.toggle(this.classes.active, !isActive);
      trigger.setAttribute('aria-expanded', String(!isActive));

      // Notify the search panel so it can close when a submenu opens.
      if (!isActive) {
        document.dispatchEvent(new CustomEvent('wlsn:nav:open'));
      }
    }

    handleItemMouseEnter(event) {
      if (!this.isDesktop) return;

      const item = event.currentTarget;
      if (!item.classList.contains(this.classes.hasDropdown)) return;

      this.clearHoverCloseTimer();
      this.openItem(item);
    }

    handleItemMouseLeave() {
      if (!this.isDesktop) return;
      this.scheduleHoverClose();
    }

    handleNavMouseEnter() {
      if (!this.isDesktop) return;
      this.clearHoverCloseTimer();
    }

    openItem(item) {
      this.navItems.forEach((navItem) => {
        const isTarget = navItem === item;
        navItem.classList.toggle(this.classes.active, isTarget);
        navItem.querySelectorAll(`.${this.classes.navTrigger}[aria-expanded]`).forEach((trigger) => {
          trigger.setAttribute('aria-expanded', String(isTarget));
        });
      });
    }

    scheduleHoverClose() {
      this.clearHoverCloseTimer();
      this.hoverCloseTimer = setTimeout(() => {
        this.closeAll();
      }, this.hoverCloseDelay);
    }

    clearHoverCloseTimer() {
      if (this.hoverCloseTimer) {
        clearTimeout(this.hoverCloseTimer);
        this.hoverCloseTimer = null;
      }
    }

    handleDocumentClick(event) {
      if (!this.isDesktop) return;
      if (this.contains(event.target)) return;
      this.closeAll();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      this.closeAll();
      this.setMobileMenu(false);
    }

    handleMenuToggleClick() {
      this.setMobileMenu(!this.classList.contains(this.classes.mobileMenuActive));
    }

    setMobileMenu(open) {
      this.classList.toggle(this.classes.mobileMenuActive, open);
      if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        this.setDrawerHeight();
      } else {
        this.closeAll();
        if (this.navList) this.navList.style.height = '';
      }
    }

    handleResize() {
      this.clearHoverCloseTimer();
      this.closeAll();
      this.setMobileMenu(false);
    }

    setDrawerHeight() {
      if (this.isDesktop || !this.navList) return;
      const top = this.navList.getBoundingClientRect().top;
      this.navList.style.height = `${window.innerHeight - top}px`;
    }

    closeAll() {
      this.clearHoverCloseTimer();
      this.navItems.forEach((item) => item.classList.remove(this.classes.active));
      this.triggers.forEach((trigger) => {
        if (trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
      });
    }

    isSamePageAnchorLink(link) {
      const href = link.getAttribute('href');
      if (!href || href === '#') return false;

      try {
        const url = new URL(link.href, window.location.href);
        if (!url.hash || url.hash === '#') return false;

        const normalizePath = (path) => path.replace(/\/$/, '') || '/';
        return normalizePath(url.pathname) === normalizePath(window.location.pathname);
      } catch {
        return false;
      }
    }

    handleNavLinkClick(event) {
      const link = event.target.closest('a[href]');
      if (!link || link.classList.contains(this.classes.navTrigger)) return;
      if (!this.isSamePageAnchorLink(link)) return;

      this.closeAll();
      this.setMobileMenu(false);
    }

    bindCartDrawerTrigger() {
      const cartDrawer = document.querySelector('wlsn-cart-drawer');
      if (this.cartDrawerTrigger && cartDrawer) {
        this.cartDrawerTrigger.addEventListener('click', () => {
          if (cartDrawer.hasAttribute('open')) {
            if (typeof cartDrawer.closeDrawer === 'function') {
              cartDrawer.closeDrawer();
            } else {
              cartDrawer.removeAttribute('open');
            }
            return;
          }

          this.setMobileMenu(false);

          if (typeof cartDrawer.openDrawer === 'function') {
            cartDrawer.openDrawer();
          } else {
            cartDrawer.setAttribute('open', '');
          }
        });
      }
    }
  }

  customElements.define('wlsn-header-nav', WLSNHeaderNav);
}
